<template>
  <div class="envelope-template-creation-form-actions">
    <app-button
      type="outlined"
      variant="neutral"
      @click="onCancel"
    >
      {{ $t('common.cancel') }}
    </app-button>

    <template v-if="currentStep === TemplateCreationSteps.DOCUMENTS_SELECTION">
      <div class="flex-grow-1"/>
      <app-button :disabled="!canValidateCurrentStep" @click="onNext">
        {{ $t('common.next') }}
      </app-button>
    </template>

    <template v-if="currentStep === TemplateCreationSteps.TEMPLATE_SETTINGS">
      <app-button :disabled="formMode === 'edition'" @click="onPrevious">
        {{ $t('common.previous') }}
      </app-button>

      <div class="flex-grow-1"/>

      <app-button
        :disabled="!canValidateCurrentStep"
        :loading="postEnvelopeTemplatePending"
        type="outlined"
        variant="neutral"
        @click="onSaveAndExit"
      >
        {{ $t('common.saveAndExit') }}
      </app-button>

      <app-button
        :disabled="!canValidateCurrentStep"
        :loading="postEnvelopeTemplatePending"
        @click="onNext"
      >
        {{ $t('common.next') }}
      </app-button>
    </template>

    <template v-if="currentStep === TemplateCreationSteps.SIGNATURE_TAGS">
      <app-button v-if="envelopeTemplatesToPrepareIndex === 0" @click="onPrevious">
        {{ $t('common.previous') }}
      </app-button>
      <app-button
        v-else
        type="outlined"
        variant="neutral"
        @click="onPrevious"
      >
        <app-material-icon
          name="arrow_back"
          class="mr-2"
        />
        {{ $t('common.previous') }}
      </app-button>

      <div class="flex-grow-1"/>

      <app-button
        v-if="!isPreparationFinished"
        type="outlined"
        variant="neutral"
        @click="onNext"
      >
        {{ $t('common.next') }}
        <app-material-icon
          name="arrow_forward"
          class="ml-2"
        />
      </app-button>
      <app-button v-else @click="onNext">
        {{ $t('common.register') }}
      </app-button>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapState } from 'vuex'

import { type FormMode } from '@/common/utils/form'
import { typedMapActions } from '@/common/utils/store'
import { toast } from '@/common/utils/toast'
import type { EnvelopeTemplateToPrepare } from '@/project/views/EnvelopeTemplateAdd.vue'
import { TemplateCreationSteps } from '@/project/views/EnvelopeTemplateAdd.vue'
import { ENVELOPE_TEMPLATE_UPDATE_ROUTE_NAME } from '@/router/routerData'
import type { EnvelopeTemplatesActionType } from '@/store/modules/envelope-templates/action_types'

export default defineComponent({
  name: 'EnvelopeTemplateAddFormActions',
  props: {
    currentStep: {
      type: Number as PropType<TemplateCreationSteps>,
      required: true,
    },
    envelopeTemplatesToPrepare: {
      type: Array as PropType<Array<EnvelopeTemplateToPrepare>>,
      required: true,
    },
    envelopeTemplatesToPrepareIndex: {
      type: Number,
      required: true,
    },
    isFormValid: {
      type: Boolean,
      required: true,
    },
    formMode: {
      type: String as PropType<FormMode>,
      default: 'creation',
    },
  },
  computed: {
    ...mapGetters('signingChecklist', ['queuedDocuments']),
    ...mapState('envelopeTemplates', ['envelopeTemplateDrafts', 'postEnvelopeTemplatePending']),
    TemplateCreationSteps () { return TemplateCreationSteps },
    canValidateCurrentStep (): boolean {
      if (this.currentStep === TemplateCreationSteps.DOCUMENTS_SELECTION) {
        return this.envelopeTemplateDrafts.length > 0 && this.queuedDocuments.length === 0
      }
      if (this.currentStep === TemplateCreationSteps.TEMPLATE_SETTINGS) {
        return this.isFormValid
      }
      if (this.currentStep === TemplateCreationSteps.SIGNATURE_TAGS) {
        return true
      }

      return false
    },
    isPreparationFinished () {
      if (this.envelopeTemplatesToPrepareIndex === (this.envelopeTemplatesToPrepare.length - 1)) return true

      return false
    },
    envelopeTemplateToPrepare (): EnvelopeTemplateToPrepare {
      return this.envelopeTemplatesToPrepare[this.envelopeTemplatesToPrepareIndex]
    },
  },
  methods: {
    ...typedMapActions<EnvelopeTemplatesActionType>('envelopeTemplates', ['UPDATE_DRAFT_TEMPLATE_DATA']),
    onCancel () {
      this.$emit('cancel')
    },
    onNext () {
      if (this.currentStep === TemplateCreationSteps.DOCUMENTS_SELECTION) {
        this.$emit('next')
      }

      if (this.currentStep === TemplateCreationSteps.TEMPLATE_SETTINGS) {
        this.$emit('save-and-next')
      }

      if (this.currentStep === TemplateCreationSteps.SIGNATURE_TAGS) {
        if (this.isPreparationFinished) {
          this.$emit('exit')
        } else {
          this.$emit('next-document')
        }
      }
    },
    async onPrevious () {
      if (this.currentStep === TemplateCreationSteps.SIGNATURE_TAGS) {
        try {
          await this.UPDATE_DRAFT_TEMPLATE_DATA(this.envelopeTemplateToPrepare.id)

          // redirect to form in edition mode
          if (this.formMode === 'creation') {
            await this.$router.push({
                name: ENVELOPE_TEMPLATE_UPDATE_ROUTE_NAME,
                query: { envelopeTemplateId: this.envelopeTemplateToPrepare.id.toString() },
            })
          }
        } catch (error) {
          console.error(error)
          toast.error(this.$t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddFormActions.updateDraftError'))
        }

        if (this.envelopeTemplatesToPrepareIndex !== 0) {
          this.$emit('previous-document')
          return
        }
      }

      this.$emit('previous')
    },
    onSaveAndExit () {
      this.$emit('save-and-exit')
    },
  },
})
</script>

<style scoped lang="scss">
.envelope-template-creation-form-actions {
  column-gap: 16px;
  display: flex;
}
</style>
